import { useState, useCallback, useEffect } from "react";
import {
  useSignMessage,
  useConnection,
  useDisconnect,
  type Connector,
} from "wagmi";
import { signMessage, watchConnection } from "@wagmi/core";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ApiLogin, ApiGetUserInfo } from "@/api";
import { showError, showLoading, dismissToast, showSuccess } from "@/lib/toast";
import { saveToken, getToken, clearToken } from "@/lib/auth";
import { useUserStore } from "@/store/useUserStore";
import { getSavedInviteCode, clearInviteCode } from "@/lib/invite";
import { wagmiConfig } from "@/config";
import {
  registerLogoutHandler,
  unregisterLogoutHandler,
} from "@/lib/logoutRegistry";

const SIGN_MESSAGE = "Welcome to TradingSkill";

// 获取客户端信息
const getClientInfo = () => {
  const userAgent = navigator.userAgent;
  let clientSystem = "Unknown";

  if (userAgent.includes("Windows")) clientSystem = "Windows";
  else if (userAgent.includes("Mac")) clientSystem = "MacOS";
  else if (userAgent.includes("Linux")) clientSystem = "Linux";
  else if (userAgent.includes("Android")) clientSystem = "Android";
  else if (userAgent.includes("iOS") || userAgent.includes("iPhone"))
    clientSystem = "iOS";

  return {
    clientId: "Web",
    clientName: "Web Browser",
    clientSystem,
  };
};

// 生成唯一客户端ID
const generateClientId = () => {
  const id = `web`;
  localStorage.setItem("client_id", id);
  return id;
};

interface UseLoginOptions {
  onSuccess?: () => void;
  onNeedInviteCode?: () => void;
}

export const useLogin = (options?: UseLoginOptions) => {
  const { t } = useTranslation();
  const { address, isConnected, connector } = useConnection();
  const { disconnect } = useDisconnect();
  const { signMessageAsync } = useSignMessage();
  const { setUserInfo, clearUserInfo, userInfo } = useUserStore();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [needInviteCode, setNeedInviteCode] = useState(false);

  const logout = useCallback(
    (opts?: { silent?: boolean }) => {
      disconnect?.();
      clearToken();
      clearUserInfo();
      queryClient.clear();

      if (!opts?.silent) {
        showSuccess(t("toast.login.logoutSuccess"));
      }
    },
    [disconnect, clearUserInfo, queryClient, t]
  );

  const login = useCallback(
    async (
      inviteCode: string = "",
      overrides?: { address?: `0x${string}`; connector?: Connector }
    ) => {
      const targetAddress = overrides?.address || address;
      const targetConnector = overrides?.connector || connector;

      if (!targetAddress) {
        showError(t("toast.common.connectWalletFirst"));
        return false;
      }

      if (!targetConnector) {
        showError(t("toast.login.connectorNotReady"));
        return false;
      }

      setIsLoading(true);
      try {
        console.log("login", targetAddress, targetConnector);
        // 1. 发起签名
        showLoading(t("toast.login.signing"), "sign");

        // 如果传入了 connector，使用 @wagmi/core 的 signMessage action
        let signature: string;
        if (overrides?.connector) {
          console.log("signMessage1", overrides.connector);
          signature = await signMessage(wagmiConfig, {
            connector: targetConnector,
            message: SIGN_MESSAGE,
          });
        } else {
          console.log("signMessage2", signMessageAsync);
          // 否则使用 hook 的 signMessageAsync
          signature = await signMessageAsync({ message: SIGN_MESSAGE });
        }

        console.log(signature, "signature");
        dismissToast("sign");

        // 2. 获取客户端信息
        const clientInfo = getClientInfo();

        // 3. 如果没有传入邀请码，尝试从已保存的邀请码中获取
        const finalInviteCode = inviteCode || getSavedInviteCode() || "";

        console.log("登录使用的邀请码:", finalInviteCode);

        // 4. 调用登录接口
        showLoading(t("toast.login.logging"), "login");
        const response = await ApiLogin({
          address: targetAddress,
          signature,
          message: SIGN_MESSAGE,
          parentInviteCode: finalInviteCode,
          ...clientInfo,
        });

        console.log(response, "登录response");

        dismissToast("login");

        const data = response.data.data;

        // 5. 处理登录结果
        if (data.result === 0) {
          // 登录成功
          saveToken(response.data.token);

          // 6. 获取用户信息并保存到全局状态
          try {
            const userInfoResponse = await ApiGetUserInfo();
            console.log(userInfoResponse, "userInfoResponse");
            if (userInfoResponse.data) {
              setUserInfo(userInfoResponse.data);
              console.log("用户信息已保存:", userInfoResponse.data);
            }
          } catch (error) {
            console.error("获取用户信息失败:", error);
            // 不影响登录流程，继续执行
          }

          // 7. 刷新所有 React Query 数据
          queryClient.invalidateQueries();
          console.log("登录成功，已刷新所有数据");

          showSuccess(t("toast.login.success"));
          options?.onSuccess?.();
          setNeedInviteCode(false);
          return true;
        } else if (data.result === 2 || data.result === 3) {
          // 需要邀请码
          console.log("服务器返回需要邀请码, result:", data.result);
          console.log("当前邀请码:", finalInviteCode);

          if (!finalInviteCode) {
            // 没有邀请码，要求输入
            setNeedInviteCode(true);
            options?.onNeedInviteCode?.();
            showError(t("toast.login.needInviteCode"));
            return false;
          } else {
            // 有邀请码但无效
            showError(t("toast.login.invalidInviteCode"));
            // 清除无效的邀请码
            clearInviteCode();
            setNeedInviteCode(true);
            return false;
          }
        } else if (data.result === 1) {
          // 签名错误
          showError(t("toast.login.signatureFailed"));
          return false;
        } else {
          showError(t("toast.login.loginFailed"));
          return false;
        }
      } catch (error: any) {
        dismissToast("sign");
        dismissToast("login");
        console.error("登录失败:", error);

        if (
          error.message?.includes("User rejected") ||
          error.message?.includes("User denied")
        ) {
          showError(t("toast.login.userCancelled"));
        } else if (
          error.message?.includes("connector") ||
          error.message?.includes("getChainId")
        ) {
          showError(t("toast.login.connectorNotReadyRetry"));
        } else {
          showError(error.message || t("toast.login.loginFailed"));
        }
        logout({ silent: true });
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [
      address,
      signMessageAsync,
      options,
      connector,
      setUserInfo,
      queryClient,
      logout,
      t,
    ]
  );

  // 供 App 内唯一 NoAuthSubscriber 调用 dispatchLogout → 执行此处注册的 logout
  useEffect(() => {
    registerLogoutHandler(logout);
    return () => unregisterLogoutHandler(logout);
  }, [logout]);

  useEffect(() => {
    const unwatch = watchConnection(wagmiConfig, {
      onChange(connection, prevConnection) {
        // 断开链接
        if (connection.isDisconnected) {
          logout({ silent: true });
        }

        // 链接钱包 或者 切换钱包
        if (
          connection.isConnected &&
          connection.address !== prevConnection?.address
        ) {
          // 如果之前有地址且已登录，说明是切换地址，需要退出并提示
          if (getToken() && userInfo?.address !== connection.address) {
            logout({ silent: true });
            showError(t("toast.login.walletSwitched"));
          } else {
            // 否则是首次连接，自动登录
            login("", {
              address: connection.address,
              connector: connection.connector,
            });
          }
        }
      },
    });
    return () => unwatch();
  }, []);
  // useEffect(() => {
  //   const handleStorageChange = (e: StorageEvent) => {
  //     console.log('handleStorageChange', e);
  //     if (!localStorage.getItem('token')) {
  //       console.log('handleStorageChange 检测到 token 被清除，自动断开钱包连接');
  //       logout({ silent: true })
  //     }
  //   }
  //   window.addEventListener('storage', handleStorageChange);
  //   return () => window.removeEventListener('storage', handleStorageChange);
  // }, []);

  const getUserInfo = async () => {
    if (getToken() && address) {
      const userInfoResponse = await ApiGetUserInfo();
      if (userInfoResponse.data) {
        const userAddress = userInfoResponse?.data?.address;
        console.log(address, userAddress, "address check");
        if (
          userInfoResponse?.data?.address?.toLowerCase() ===
          address?.toLowerCase()
        ) {
          return;
        }
        console.log("地址已经发生变化", address);
        logout({ silent: true });
      }
    }
  };

  useEffect(() => {
    getUserInfo();
  }, [address]);

  return {
    login,
    logout,
    isLoading,
    needInviteCode,
    setNeedInviteCode,
    isLoggedIn: !!getToken() && isConnected,
  };
};
